import React, { useState, useEffect } from "react";
import { useAuth } from "../../../../hooks/useAuth";
import {
  getMissionServiceAPI,
  getMissionActionAPI,
  getMemberAdAPI,
  getKeywordNameAPI,
  getPriceGradeDetailAPI,
  getUserDetailAPI,
} from "../../../../api/index";
import { convertUTCToKoreaTime } from "../../../../utils/helpers";
import checkAdvertisementIconActive from "../../../../assets/icons/checkAdvertisementActive.svg";
import { COLORS } from "../../../../constants";
import ShoppingAdDetail from "./components/ShoppingAdDetail";
import NormalAdDetail from "./components/NormalAdDetail";
import CoupangAdDetail from "./components/CoupangAdDetail";
import PlaceAdDetail from "./components/PlaceAdDetail";
import AppReviewAdDetail from "./components/AppReviewAdDetail";
import AdExtensionModal from "./components/AdExtensionModal";
import RankCheckModal from "./components/RankCheckModal";
import QuantityIncreaseHistoryModal from "./components/QuantityIncreaseHistoryModal";
import AdEndHistoryModal from "./components/AdEndHistoryModal";
import "./MyAdvertisementMobile.css";

const MyAdvertisementMobile = () => {
  const { user } = useAuth();
  const [selectedMainTab, setSelectedMainTab] = useState("naver");
  const [selectedSubTab, setSelectedSubTab] = useState("shopping");

  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("missionName");
  const [selectedAds, setSelectedAds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [isUrgent, setIsUrgent] = useState(false);
  const [selectedAdData, setSelectedAdData] = useState(null);

  const [isBulkExtensionModalOpen, setIsBulkExtensionModalOpen] =
    useState(false);
  const [isRankCheckModalOpen, setIsRankCheckModalOpen] = useState(false);
  const [selectedAdForRankCheck, setSelectedAdForRankCheck] = useState(null);
  const [isQuantityIncreaseHistoryOpen, setIsQuantityIncreaseHistoryOpen] =
    useState(false);
  const [isAdEndHistoryOpen, setIsAdEndHistoryOpen] = useState(false);

  const [missionServices, setMissionServices] = useState([]);
  const [missionActions, setMissionActions] = useState([]);
  const [adData, setAdData] = useState([]);
  const [keywordData, setKeywordData] = useState({});
  const [priceGradeData, setPriceGradeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState(null);

  const statusOptions = [
    { value: "ALL", label: "전체" },
    { value: "APPROVED", label: "진행중" },
    { value: "PAUSED", label: "일시정지" },
    { value: "PENDING", label: "승인대기중" },
    { value: "REJECTED", label: "거절" },
    { value: "DONE", label: "종료" },
  ];

  const convertAdDatesToKoreaTime = (ads) => {
    return ads.map((ad) => {
      const convertedAd = { ...ad };

      if (ad.startDate) {
        const koreaStartDate = convertUTCToKoreaTime(ad.startDate);
        if (koreaStartDate) {
          convertedAd.startDate = koreaStartDate.toISOString().split("T")[0];
        }
      }

      if (ad.endDate) {
        const koreaEndDate = convertUTCToKoreaTime(ad.endDate);
        if (koreaEndDate) {
          convertedAd.endDate = koreaEndDate.toISOString().split("T")[0];
        }
      }

      if (ad.createdAt) {
        const koreaCreatedAt = convertUTCToKoreaTime(ad.createdAt);
        if (koreaCreatedAt) {
          convertedAd.createdAt = koreaCreatedAt.toISOString();
        }
      }

      return convertedAd;
    });
  };

  const loadMissionServices = async () => {
    if (!user?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getMissionServiceAPI();

      if (response.data?.data) {
        const services = response.data.data.map((service) => ({
          id: service.ms_id,
          name: service.name + " 광고",
          originalName: service.name,
          ms_id: service.ms_id,
        }));
        setMissionServices(services);

        if (services.length > 0) {
          const firstService = services[0];
          setSelectedMainTab(firstService.id);
          await loadMissionActions(firstService.ms_id);
        }
      }
    } catch (err) {
      console.error("미션 서비스 로딩 실패:", err);
      setError("미션 서비스 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const loadMissionActions = async (ms_id) => {
    if (!user?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getMissionActionAPI(ms_id);

      if (response.data?.data) {
        const actions = response.data.data.map((action) => ({
          id: action.ma_id,
          name: action.name,
          ma_id: action.ma_id,
        }));

        const sortedActions = actions.sort((a, b) => {
          const order = [1, 2, 3, 4];
          return order.indexOf(a.ma_id) - order.indexOf(b.ma_id);
        });

        setMissionActions(sortedActions);

        if (sortedActions.length > 0) {
          const firstAction = sortedActions[0];
          setSelectedSubTab(firstAction.id);
          await loadAdDataAndKeywords(ms_id, firstAction.ma_id, 1);
        }
      }
    } catch (err) {
      console.error("미션 액션 로딩 실패:", err);
      setError("미션 액션 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const buildKeywordDataByAdId = (keywords) => {
    const keywordMap = {};
    keywords.forEach((keyword) => {
      const adId = keyword.ad_id;
      if (!keywordMap[adId]) {
        keywordMap[adId] = [];
      }
      keywordMap[adId].push(keyword);
    });
    return keywordMap;
  };

  const fetchAllFilteredAdIds = async (memberId, apiParams) => {
    const requestMemberId = user.role === "CHECKER" ? null : memberId;
    const adLimit = 100;
    const firstPageResponse = await getMemberAdAPI(
      requestMemberId,
      1,
      adLimit,
      apiParams
    );

    const firstAds = firstPageResponse.data?.data || [];
    const firstPagination = firstPageResponse.data?.pagination || {};
    const totalFromPagination = Number(firstPagination.total) || firstAds.length;
    const totalPagesFromServer = Number(firstPagination.totalPages);
    const effectiveLimit = Number(firstPagination.limit) || adLimit;
    const totalPages = Number.isFinite(totalPagesFromServer) && totalPagesFromServer > 0
      ? totalPagesFromServer
      : Math.max(1, Math.ceil(totalFromPagination / effectiveLimit));

    const allAdIds = new Set(firstAds.map((ad) => ad.ad_id).filter(Boolean));

    for (let currentPage = 2; currentPage <= totalPages; currentPage += 1) {
      const pageResponse = await getMemberAdAPI(
        requestMemberId,
        currentPage,
        adLimit,
        apiParams
      );
      const pageAds = pageResponse.data?.data || [];
      pageAds.forEach((ad) => {
        if (ad.ad_id) {
          allAdIds.add(ad.ad_id);
        }
      });
    }

    return Array.from(allAdIds);
  };

  const fetchKeywordsByAdIds = async (adIds) => {
    if (!adIds.length) {
      return {};
    }

    const adIdChunkSize = 50;
    const keywordLimit = 100;
    const allKeywords = [];

    for (let i = 0; i < adIds.length; i += adIdChunkSize) {
      const adIdChunk = adIds.slice(i, i + adIdChunkSize);
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const keywordResponse = await getKeywordNameAPI(
          null,
          adIdChunk,
          currentPage,
          keywordLimit
        );
        const chunkKeywords = keywordResponse.data?.data || [];
        const pagination = keywordResponse.data?.pagination || {};
        allKeywords.push(...chunkKeywords);

        const totalPagesFromServer = Number(pagination.totalPages);
        if (Number.isFinite(totalPagesFromServer) && totalPagesFromServer > 0) {
          hasMore = currentPage < totalPagesFromServer;
        } else {
          hasMore = chunkKeywords.length === keywordLimit;
        }
        currentPage += 1;
      }
    }

    return buildKeywordDataByAdId(allKeywords);
  };

  const loadAdDataAndKeywords = async (
    missionType,
    missionTemplate,
    page = 1
  ) => {
    if (!user?.id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // CHECKER 역할일 때는 checkerId 사용, 그 외에는 webMemberId 사용
      const memberId = user.role === "CHECKER" ? user.id : (user.webMemberId || user.id);
      const apiParams = {
        missionType: missionType,
        missionTemplate: missionTemplate,
        status: statusFilter !== "ALL" ? statusFilter : undefined,
        [searchType]: searchTerm || undefined,
        urgent: isUrgent ? true : undefined,
      };

      // CHECKER 역할일 때는 checkerId를 파라미터로 추가
      if (user.role === "CHECKER") {
        apiParams.checkerId = memberId;
      }

      const adResponse = await getMemberAdAPI(
        user.role === "CHECKER" ? null : memberId, // CHECKER일 때는 첫 번째 파라미터를 null로
        page,
        10,
        apiParams
      );

      if (adResponse.data?.data) {
        const ads = adResponse.data.data;
        const paginationInfo = adResponse.data.pagination;

        const convertedAds = convertAdDatesToKoreaTime(ads);
        setAdData(convertedAds);
        setPagination(paginationInfo);

        if (paginationInfo) {
          setCurrentPage(paginationInfo.page || 1);
          const actualLimit = 10;
          const total = paginationInfo.total || ads.length;
          const calculatedTotalPages = Math.ceil(total / actualLimit);

          setTotalPages(calculatedTotalPages);
          setTotalItems(total);
        }

        try {
          const allAdIds = await fetchAllFilteredAdIds(memberId, apiParams);
          const newKeywordData = await fetchKeywordsByAdIds(allAdIds);
          setKeywordData(newKeywordData);
        } catch (err) {
          console.error("❌ 키워드 데이터 로딩 실패:", err);
          setKeywordData({});
        }
      }

      if (user?.price_grade_id) {
        try {
          const priceResponse = await getPriceGradeDetailAPI(
            user.price_grade_id
          );
          setPriceGradeData(priceResponse.data?.data);
        } catch (err) {
          console.error("가격 등급 데이터 로딩 실패:", err);
        }
      }
    } catch (err) {
      console.error("광고 데이터 로딩 실패:", err);
      setError("광고 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleMainTabChange = async (tabId) => {
    setSelectedMainTab(tabId);
    setSelectedAds([]);
    setCurrentPage(1);

    if (user?.id) {
      const selectedService = missionServices.find(
        (service) => service.id === tabId
      );

      if (selectedService) {
        await loadMissionActions(selectedService.ms_id);
      }
    }
  };

  const handleSubTabChange = async (subTabId) => {
    setSelectedSubTab(subTabId);
    setSelectedAds([]);
    setCurrentPage(1);

    if (user?.id) {
      const selectedAction = missionActions.find(
        (action) => action.id === subTabId
      );

      if (selectedAction) {
        const selectedService = missionServices.find(
          (service) => service.id === selectedMainTab
        );
        await loadAdDataAndKeywords(
          selectedService?.ms_id || 1,
          selectedAction.ma_id,
          1
        );
      }
    }
  };

  const handleSearch = async () => {
    if (!user?.id || !selectedMainTab || !selectedSubTab) {
      return;
    }

    const currentService = missionServices.find(
      (s) => s.id === selectedMainTab
    ) || { ms_id: 1 };
    const currentAction = missionActions.find(
      (a) => a.id === selectedSubTab
    ) || { ma_id: 1 };

    await loadAdDataAndKeywords(
      currentService.ms_id,
      currentAction.ma_id,
      1
    );
  };

  const handleViewAd = (ad) => {
    setSelectedAdData(ad);
  };

  const handleCloseDetail = () => {
    setSelectedAdData(null);
  };

  const handleBulkExtend = () => {
    if (selectedAds.length === 0) {
      alert("연장할 광고를 선택해주세요.");
      return;
    }

    setIsBulkExtensionModalOpen(true);
  };

  // 페이지 변경 핸들러
  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }

    setCurrentPage(newPage);
    setSelectedAds([]); // 페이지 변경 시 선택 상태 초기화

    // 현재 선택된 서비스와 액션의 ID 찾기
    const currentService = missionServices.find(
      (s) => s.id === selectedMainTab
    ) || { ms_id: 1 };
    const currentAction = missionActions.find(
      (a) => a.id === selectedSubTab
    ) || { ma_id: 1 };

    await loadAdDataAndKeywords(
      currentService.ms_id,
      currentAction.ma_id,
      newPage
    );
  };

  const handleOpenRankCheckModal = (ad) => {
    setSelectedAdForRankCheck(ad);
    setIsRankCheckModalOpen(true);
  };

  const handleCloseRankCheckModal = () => {
    setIsRankCheckModalOpen(false);
    setSelectedAdForRankCheck(null);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "APPROVED":
        return "status-progress";
      case "PENDING":
        return "status-pending";
      case "PAUSED":
        return "status-paused";
      case "REJECTED":
        return "status-rejected";
      case "REFUND":
        return "status-refund";
      case "DONE":
        return "status-ended";
      default:
        return "";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "PENDING":
        return "승인대기중";
      case "REJECTED":
        return "반려";
      case "REFUND":
        return "환불";
      case "DONE":
        return "종료";
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.getFullYear()}. ${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}. ${String(date.getDate()).padStart(2, "0")}`;
  };

  const calculateRemainingDays = (endDate) => {
    if (!endDate) return null;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRemainingDaysClass = (days) => {
    if (days === null) return "";
    if (days < 0) return "remaining-days-negative";
    if (days <= 3) return "remaining-days-urgent";
    return "remaining-days-normal";
  };

  useEffect(() => {
    if (user?.id) {
      loadMissionServices();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && selectedMainTab && selectedSubTab) {
      const currentService = missionServices.find(
        (s) => s.id === selectedMainTab
      ) || { ms_id: 1 };
      const currentAction = missionActions.find(
        (a) => a.id === selectedSubTab
      ) || { ma_id: 1 };

      loadAdDataAndKeywords(
        currentService.ms_id,
        currentAction.ma_id,
        currentPage
      );
    }
  }, [statusFilter, isUrgent, user?.id]);

  const mainTabs = user?.id
    ? missionServices.map((service) => ({
        id: service.id,
        label: service.name,
        active: selectedMainTab === service.id,
      }))
    : [
        { id: "naver", label: "네이버 광고", active: selectedMainTab === "naver" },
        { id: "review", label: "리뷰 광고", active: selectedMainTab === "review" },
      ];

  const getSubTabs = () => {
    return missionActions.map((action) => ({
      id: action.id,
      label: action.name,
      active: selectedSubTab === action.id,
    }));
  };

  const subTabs = user?.id
    ? getSubTabs()
    : selectedMainTab === "naver"
    ? [
        { id: "shopping", label: "쇼핑 방문", active: selectedSubTab === "shopping" },
        {
          id: "shopping_origin",
          label: "쇼핑(원부) 방문",
          active: selectedSubTab === "shopping_origin",
        },
        { id: "place", label: "플레이스 방문", active: selectedSubTab === "place" },
        { id: "save", label: "플레이스 저장", active: selectedSubTab === "save" },
      ]
    : [{ id: "app", label: "앱 리뷰", active: selectedSubTab === "app" }];

  return (
    <div className="my-ad-mobile-container">
      {/* 헤더 */}
      {!selectedAdData && !isRankCheckModalOpen && (
        <div className="my-ad-mobile-header">
          <div className="my-ad-mobile-header-content">
            <img
              src={checkAdvertisementIconActive}
              alt="내 광고 목록"
              className="my-ad-mobile-title-icon"
            />
            <h1 className="my-ad-mobile-title">내 광고 목록</h1>
          </div>
          <div className="my-ad-mobile-header-actions">
            <button
              type="button"
              className="my-ad-mobile-quantity-history-btn"
              onClick={() => setIsQuantityIncreaseHistoryOpen(true)}
              disabled={!user?.id}
            >
              수량증가 내역
            </button>
            <button
              type="button"
              className="my-ad-mobile-quantity-history-btn"
              onClick={() => setIsAdEndHistoryOpen(true)}
              disabled={!user?.id}
            >
              광고 종료 신청 내역
            </button>
          </div>
        </div>
      )}

      {/* 탭 컨테이너 */}
      {!selectedAdData && !isRankCheckModalOpen && (
        <div className="my-ad-mobile-tabs-wrapper">
          {/* 메인 탭 */}
          <div className="my-ad-mobile-main-tabs">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                className={`my-ad-mobile-main-tab ${
                  tab.active ? "active" : ""
                }`}
                onClick={() => handleMainTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* 서브 탭 */}
          {subTabs.length > 0 && (
            <div className="my-ad-mobile-sub-tabs">
              {subTabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`my-ad-mobile-sub-tab ${
                    tab.active ? "active" : ""
                  }`}
                  onClick={() => handleSubTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 필터 및 검색 영역 */}
      {!selectedAdData && !isRankCheckModalOpen && (
        <div className="my-ad-mobile-filters">
          <div className="my-ad-mobile-filter-row">
            <label className="my-ad-mobile-filter-label">과거 주문 불러오기</label>
            <select
              className="my-ad-mobile-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="my-ad-mobile-filter-row">
            <label className="my-ad-mobile-checkbox-label">
              <input
                type="checkbox"
                checked={isUrgent}
                onChange={(e) => setIsUrgent(e.target.checked)}
                className="my-ad-mobile-checkbox"
              />
              <span className="my-ad-mobile-checkbox-text">마감임박</span>
            </label>
          </div>

          <div className="my-ad-mobile-filter-row">
            <select
              className="my-ad-mobile-filter-select"
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
            >
              <option value="missionName">상품명</option>
              <option value="storeName">스토어 이름</option>
            </select>
            <div className="my-ad-mobile-search-input">
              <input
                type="text"
                placeholder={
                  searchType === "missionName"
                    ? "상품명으로 찾기"
                    : "스토어 이름으로 찾기"
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
              <button className="my-ad-mobile-search-btn" onClick={handleSearch}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
          </div>

          <button
            className="my-ad-mobile-bulk-extend-btn"
            onClick={handleBulkExtend}
            disabled={!user?.id || selectedAds.length === 0}
          >
            광고 일괄 연장
          </button>
        </div>
      )}

      {/* 광고 목록 */}
      {!isRankCheckModalOpen && (loading ? (
        <div className="my-ad-mobile-loading">광고 데이터를 불러오는 중...</div>
      ) : error ? (
        <div className="my-ad-mobile-error">{error}</div>
      ) : selectedAdData ? (
        <div className="my-ad-mobile-detail-view">
          <div className="my-ad-mobile-detail-header">
            <h3 className="my-ad-mobile-detail-title">광고 상세 정보</h3>
            <button
              onClick={handleCloseDetail}
              className="my-ad-mobile-back-btn"
            >
              ← 목록으로 돌아가기
            </button>
          </div>
          <div className="my-ad-mobile-detail-content">
            {/* 쿠팡 방문 (missionTemplate: 13) - 쿠팡 전용 상세 */}
            {(selectedSubTab === 13 ||
              selectedAdData?.missionTemplate === 13) && (
              <CoupangAdDetail
                isOpen={true}
                onClose={handleCloseDetail}
                adData={selectedAdData}
                keywordData={keywordData[selectedAdData?.ad_id] || []}
                priceGradeData={priceGradeData}
                getUserDetailAPI={getUserDetailAPI}
                onSave={async () => {
                  const currentService = missionServices.find(
                    (s) => s.id === selectedMainTab
                  ) || { ms_id: 1 };
                  const currentAction = missionActions.find(
                    (a) => a.id === selectedSubTab
                  ) || { ma_id: 1 };

                  await loadAdDataAndKeywords(
                    currentService.ms_id,
                    currentAction.ma_id,
                    currentPage
                  );

                  const updatedAd = adData.find(
                    (ad) => ad.ad_id === selectedAdData.ad_id
                  );
                  if (updatedAd) {
                    setSelectedAdData(updatedAd);
                  }
                }}
              />
            )}

            {/* 쇼핑 관련 광고 (쇼핑 방문, 쇼핑 원부 방문) */}
            {(selectedSubTab === "shopping" ||
              selectedSubTab === 1 ||
              selectedSubTab === 2 ||
              selectedSubTab === "shopping_origin") && (
              <ShoppingAdDetail
                isOpen={true}
                onClose={handleCloseDetail}
                adData={selectedAdData}
                keywordData={keywordData[selectedAdData?.ad_id] || []}
                priceGradeData={priceGradeData}
                getUserDetailAPI={getUserDetailAPI}
                onSave={async () => {
                  const currentService = missionServices.find(
                    (s) => s.id === selectedMainTab
                  ) || { ms_id: 1 };
                  const currentAction = missionActions.find(
                    (a) => a.id === selectedSubTab
                  ) || { ma_id: 1 };

                  await loadAdDataAndKeywords(
                    currentService.ms_id,
                    currentAction.ma_id,
                    currentPage
                  );

                  const updatedAd = adData.find(
                    (ad) => ad.ad_id === selectedAdData.ad_id
                  );
                  if (updatedAd) {
                    setSelectedAdData(updatedAd);
                  }
                }}
              />
            )}

            {(selectedSubTab === "place" ||
              selectedSubTab === 3 ||
              selectedSubTab === "save" ||
              selectedSubTab === 4) && (
              <PlaceAdDetail
                isOpen={true}
                onClose={handleCloseDetail}
                adData={selectedAdData}
                keywordData={keywordData[selectedAdData?.ad_id] || []}
                priceGradeData={priceGradeData}
                getUserDetailAPI={getUserDetailAPI}
                onSave={async () => {
                  const currentService = missionServices.find(
                    (s) => s.id === selectedMainTab
                  ) || { ms_id: 1 };
                  const currentAction = missionActions.find(
                    (a) => a.id === selectedSubTab
                  ) || { ma_id: 1 };

                  await loadAdDataAndKeywords(
                    currentService.ms_id,
                    currentAction.ma_id,
                    currentPage
                  );

                  const updatedAd = adData.find(
                    (ad) => ad.ad_id === selectedAdData.ad_id
                  );
                  if (updatedAd) {
                    setSelectedAdData(updatedAd);
                  }
                }}
              />
            )}

            {(selectedSubTab === "app" ||
              selectedSubTab === 5 ||
              selectedSubTab === 6) && (
              <AppReviewAdDetail
                isOpen={true}
                onClose={handleCloseDetail}
                adData={selectedAdData}
                keywordData={keywordData[selectedAdData?.ad_id] || []}
                priceGradeData={priceGradeData}
                getUserDetailAPI={getUserDetailAPI}
                onSave={async () => {
                  const currentService = missionServices.find(
                    (s) => s.id === selectedMainTab
                  ) || { ms_id: 1 };
                  const currentAction = missionActions.find(
                    (a) => a.id === selectedSubTab
                  ) || { ma_id: 1 };

                  await loadAdDataAndKeywords(
                    currentService.ms_id,
                    currentAction.ma_id,
                    currentPage
                  );

                  const updatedAd = adData.find(
                    (ad) => ad.ad_id === selectedAdData.ad_id
                  );
                  if (updatedAd) {
                    setSelectedAdData(updatedAd);
                  }
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="my-ad-mobile-ad-list">
          {adData.length === 0 ? (
            <div className="my-ad-mobile-no-data">등록된 광고가 없습니다</div>
          ) : (
            adData.map((ad) => {
              const remainingDays = calculateRemainingDays(ad.endDate);
              const statusClass = getStatusClass(ad.status);
              const statusText = getStatusText(ad.status);

              return (
                <div key={ad.ad_id || ad.id} className="my-ad-mobile-ad-card">
                  <div className="my-ad-mobile-ad-header">
                    <div className="my-ad-mobile-ad-info">
                      <div className="my-ad-mobile-ad-thumbnail">
                        <img
                          src={ad.imageUrl || "/header_icon.ico"}
                          alt={ad.missionName}
                          className="my-ad-mobile-ad-thumbnail-img"
                        />
                      </div>
                      <div className="my-ad-mobile-ad-text">
                        <div className="my-ad-mobile-ad-store">
                          {ad.storeName || "스토어 이름"}
                        </div>
                        <div className="my-ad-mobile-ad-name">{ad.missionName}</div>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedAds.includes(ad.ad_id || ad.id)}
                      onChange={() => {
                        if (selectedAds.includes(ad.ad_id || ad.id)) {
                          setSelectedAds(
                            selectedAds.filter((id) => id !== (ad.ad_id || ad.id))
                          );
                        } else {
                          setSelectedAds([...selectedAds, ad.ad_id || ad.id]);
                        }
                      }}
                      className="my-ad-mobile-ad-checkbox"
                    />
                  </div>

                  <div className="my-ad-mobile-ad-details">
                    <div className="my-ad-mobile-ad-detail-row">
                      <div className="my-ad-mobile-ad-detail-item">
                        <div className="my-ad-mobile-ad-detail-label">메인 키워드</div>
                        <div className="my-ad-mobile-ad-detail-value">
                          {ad.mainKeyword || "-"}
                        </div>
                      </div>
                      <div className="my-ad-mobile-ad-detail-item">
                        <div className="my-ad-mobile-ad-detail-label">신청 수량</div>
                        <div className="my-ad-mobile-ad-detail-value">
                          {ad.totalQuantity
                            ? typeof ad.totalQuantity === "number"
                              ? ad.totalQuantity.toLocaleString()
                              : ad.totalQuantity
                            : "-"}
                        </div>
                      </div>
                    </div>

                    <div className="my-ad-mobile-ad-detail-row">
                      <div className="my-ad-mobile-ad-detail-item">
                        <div className="my-ad-mobile-ad-detail-label">시작 순위</div>
                        <div className="my-ad-mobile-ad-detail-value">
                          {(() => {
                            const keywords = keywordData[ad.ad_id] || [];
                            const mainKeyword = keywords.find((k) => k.type === "MAIN");
                            const rank = mainKeyword?.initialRank || ad.initialRank;
                            return rank ? `${rank}위` : "-";
                          })()}
                        </div>
                      </div>
                      <div className="my-ad-mobile-ad-detail-item">
                        <div className="my-ad-mobile-ad-detail-label">현재 순위</div>
                        <div
                          className="my-ad-mobile-ad-detail-value my-ad-mobile-ad-rank-link"
                          onClick={() => handleOpenRankCheckModal(ad)}
                        >
                          {(() => {
                            const keywords = keywordData[ad.ad_id] || [];
                            const mainKeyword = keywords.find((k) => k.type === "MAIN");
                            const rank = mainKeyword?.currentRank || ad.currentRank;
                            return rank ? `${rank}위` : "-";
                          })()}
                        </div>
                      </div>
                    </div>

                    <div className="my-ad-mobile-ad-detail-row">
                      <div className="my-ad-mobile-ad-detail-item">
                        <div className="my-ad-mobile-ad-detail-label">시작일</div>
                        <div className="my-ad-mobile-ad-detail-value">
                          {formatDate(ad.startDate)}
                        </div>
                      </div>
                      <div className="my-ad-mobile-ad-detail-item">
                        <div className="my-ad-mobile-ad-detail-label">종료일</div>
                        <div className="my-ad-mobile-ad-detail-value">
                          {formatDate(ad.endDate)}
                        </div>
                      </div>
                    </div>

                    <div className="my-ad-mobile-ad-detail-row">
                      <div className="my-ad-mobile-ad-detail-item">
                        <div className="my-ad-mobile-ad-detail-label">잔여기간</div>
                        <div
                          className={`my-ad-mobile-ad-detail-value my-ad-mobile-remaining-days ${getRemainingDaysClass(
                            remainingDays
                          )}`}
                        >
                          {remainingDays !== null
                            ? remainingDays < 0
                              ? `종료`
                              : `D-${remainingDays}`
                            : "-"}
                        </div>
                      </div>
                      <div className="my-ad-mobile-ad-detail-item">
                        <div className="my-ad-mobile-ad-detail-label">진행상황</div>
                        <div
                          className={`my-ad-mobile-ad-detail-value my-ad-mobile-status-badge ${statusClass}`}
                        >
                          {statusText}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="my-ad-mobile-ad-divider"></div>

                  <div
                    className="my-ad-mobile-ad-view-detail"
                    onClick={() => handleViewAd(ad)}
                  >
                    상세보기
                  </div>
                </div>
              );
            })
          )}
        </div>
      ))}

      {/* 광고 일괄 연장 모달 */}
      {isBulkExtensionModalOpen && (
        <AdExtensionModal
          isOpen={isBulkExtensionModalOpen}
          onClose={() => setIsBulkExtensionModalOpen(false)}
          selectedAds={selectedAds
            .map((adId) => {
              const foundAd = adData.find((ad) => ad.ad_id === adId);
              if (!foundAd) {
                console.warn(
                  `광고 ID ${adId}에 해당하는 데이터를 찾을 수 없습니다.`
                );
                return null;
              }
              return foundAd;
            })
            .filter(Boolean)}
          onExtend={(result) => {
            if (result === "success") {
              const currentService = missionServices.find(
                (s) => s.id === selectedMainTab
              ) || { ms_id: 1 };
              const currentAction = missionActions.find(
                (a) => a.id === selectedSubTab
              ) || { ma_id: 1 };

              loadAdDataAndKeywords(
                currentService.ms_id,
                currentAction.ma_id,
                currentPage
              );

              setSelectedAds([]);
              alert("광고 연장이 성공적으로 완료되었습니다!");
            }

            setIsBulkExtensionModalOpen(false);
          }}
        />
      )}

      {/* 순위체크 모달 - 모바일에서는 페이지로 표시 */}
      {isRankCheckModalOpen && (
        <div className="my-ad-mobile-rank-check-page">
          <RankCheckModal
            isOpen={isRankCheckModalOpen}
            onClose={handleCloseRankCheckModal}
            adData={selectedAdForRankCheck}
            isMobile={true}
          />
        </div>
      )}

      {/* 수량증가 내역 모달 */}
      <QuantityIncreaseHistoryModal
        isOpen={isQuantityIncreaseHistoryOpen}
        onClose={() => setIsQuantityIncreaseHistoryOpen(false)}
        webMemberId={user?.id}
      />

      {/* 광고 종료 신청 내역 모달 */}
      <AdEndHistoryModal
        isOpen={isAdEndHistoryOpen}
        onClose={() => setIsAdEndHistoryOpen(false)}
        webMemberId={user?.webMemberId ?? user?.id}
      />

      {/* 페이징 컴포넌트 - 상세보기 모드가 아닐 때만 표시 */}
      {!selectedAdData && !isRankCheckModalOpen && user?.id && (totalPages > 1 || totalItems > 10) && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>A total of {totalItems} data</span>
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              &lt;
            </button>

            {/* 페이지 번호들 */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${
                    currentPage === pageNum ? "active" : ""
                  }`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAdvertisementMobile;

